import { useState } from "react";

import {
  ActionIcon,
  Button,
  Center,
  Container,
  Flex,
  Group,
  Stack,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { IconTrash, IconChecklist, IconPhoto } from "@tabler/icons-react";
import _ from "lodash";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppState } from "../../hooks/useAppState";
import { useLibraryState } from "../../hooks/useEditorState";
import { librarySlice } from "../../lib/state/reducer";
import { ConfirmDeletionModal } from "../editor/ConfirmDeletionModal";
import { PDFDownloadButton } from "../editor/PDFDownloadButton";

export const ManagePage = (): JSX.Element => {
  const libraryIndexesString = useAppState((state) => {
    const sorted = _.sortBy(state.library.entries, ["mtime"]).reverse();
    const indexes = sorted.map((e) =>
      _.findIndex(state.library.entries, (x) => x.present.id === e.present.id)
    );
    return indexes.join(",");
  });

  const libraryIndexes = libraryIndexesString ? libraryIndexesString.split(",") : [];

  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);

  return (
    <Container size="md" py="md" style={{ height: "100%" }}>
      <Helmet>
        <title>My Checklists</title>
      </Helmet>
      <ConfirmDeletionModal
        open={confirmDeleteIndex !== null}
        onClose={() => setConfirmDeleteIndex(null)}
        libraryEntry={confirmDeleteIndex}
      />
      {libraryIndexes.length > 0 ? (
        <Table horizontalSpacing={"sm"}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Last Updated</th>
              <th>Created</th>
              <th>
                <Center>Download PDF </Center>
              </th>
              <th>
                <Center>Delete</Center>
              </th>
            </tr>
          </thead>
          <tbody>
            {libraryIndexes.map((libraryIndex) => (
              <LibraryEntry
                key={libraryIndex}
                libraryIndex={parseInt(libraryIndex, 10)}
                onDelete={() => setConfirmDeleteIndex(parseInt(libraryIndex, 10))}
              />
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState />
      )}
    </Container>
  );
};

export const LibraryEntry = ({
  libraryIndex,
  onDelete,
}: {
  libraryIndex: number;
  onDelete: () => void;
}): JSX.Element => {
  const libraryEntry = useLibraryState((state) => state.entries[libraryIndex]);
  const reduxDispatch = useAppDispatch();
  const theme = useMantineTheme();

  return (
    <tr>
      <td>
        <Link
          to="/editor"
          onClick={() => reduxDispatch(librarySlice.actions.setEditorEntry(libraryIndex))}
          style={{
            color: theme.colors.cyan[6],
          }}
        >
          <strong>{libraryEntry.present.title || "(Untitled)"}</strong>
        </Link>
      </td>
      <td>{DateTime.fromMillis(libraryEntry.mtime).toLocaleString(DateTime.DATETIME_SHORT)}</td>
      <td>{DateTime.fromMillis(libraryEntry.ctime).toLocaleString(DateTime.DATETIME_SHORT)}</td>
      <td style={{ width: "125px" }}>
        <Center>
          <PDFDownloadButton
            iconSize={16}
            libraryIndex={libraryIndex}
            color="cyan.6"
            variant="filled"
          />
        </Center>
      </td>
      <td style={{ width: "80px" }}>
        <Center>
          <ActionIcon onClick={() => onDelete()} color="red.8" variant="filled">
            <IconTrash size={16} />
          </ActionIcon>
        </Center>
      </td>
    </tr>
  );
};

export const EmptyState = (): JSX.Element => {
  return (
    <Container size="sm" style={{ height: "100%" }}>
      <Flex direction="column" justify="center" style={{ height: "100%" }}>
        <Stack spacing="xl">
          <Text fz="lg" ta="center">
            You don't have any documents just yet. To get started, choose from a template in the
            template gallery or start a new document from scratch.
          </Text>
          <Group align="center" position="center">
            <Button
              variant="gradient"
              gradient={{ from: "cyan.4", to: "blue", deg: 45 }}
              size="md"
              radius="xl"
              leftIcon={<IconChecklist />}
              component={Link}
              to="/editor"
            >
              Start a New Document
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: "orange", to: "red", deg: 45 }}
              size="md"
              radius="xl"
              component={Link}
              to="/templates"
              leftIcon={<IconPhoto />}
            >
              Explore templates
            </Button>
          </Group>
        </Stack>
      </Flex>
    </Container>
  );
};
